import apiClient from './api.service';
import { RegisterData, LoginData, AuthResponse } from '../types/auth.types';

/**
 * Authentication Service
 * Handles all auth-related API calls
 */

class AuthService {
  /**
   * Send OTP to phone for registration verification
   * @param phone - Phone number (digits only, 10-15)
   * @returns Promise with success and optional expiresAt
   */
  async sendOtp(phone: string): Promise<{
    success: boolean;
    data?: { expiresAt: string; smsDelivered?: boolean };
  }> {
    const response = await apiClient.post<{
      success: boolean;
      data?: { expiresAt: string; smsDelivered?: boolean };
    }>('/auth/send-otp', { phone: phone.replace(/\D/g, '') });
    return response.data;
  }

  /**
   * Register a new user (requires OTP in data)
   * @param data - Registration data including otp
   * @returns Promise with auth response
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/register', data);
    return response.data;
  }

  /**
   * Login a user
   * @param data - Login credentials
   * @returns Promise with auth response
   */
  async login(data: LoginData): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/login', data);
    return response.data;
  }

  /**
   * Refresh access token
   * @param refreshToken - Refresh token
   * @returns Promise with new access token
   */
  async refreshToken(refreshToken: string): Promise<{ accessToken: string }> {
    const response = await apiClient.post('/auth/refresh', { refreshToken });
    return response.data.data;
  }

  /**
   * Logout user (optional - mainly client-side)
   * @returns Promise with response
   */
  async logout(fcmToken?: string): Promise<void> {
    await apiClient.post('/auth/logout', fcmToken ? { token: fcmToken } : {});
  }

  /**
   * Get current user info
   * @returns Promise with user data
   */
  async getCurrentUser() {
    const response = await apiClient.get('/auth/me');
    return response.data.data;
  }

  /**
   * Update user profile
   * @param data - Profile update data
   * @returns Promise with updated user data
   */
  async updateProfile(data: {
    email?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    pseudo?: string;
    birth_date?: string | null;
  }) {
    const response = await apiClient.put('/auth/profile', data);
    return response.data.data;
  }

  /**
   * Change user password
   * @param data - Password change data
   * @returns Promise with response
   */
  async changePassword(data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }) {
    const response = await apiClient.put('/auth/change-password', data);
    return response.data;
  }

  async requestPasswordReset(email: string): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  }

  async validateResetToken(token: string): Promise<{ success: boolean }> {
    const response = await apiClient.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
    return response.data;
  }

  async resetPassword(data: { token: string; password: string; confirm_password: string }): Promise<{ success: boolean }> {
    const response = await apiClient.post('/auth/reset-password', data);
    return response.data;
  }

  /**
   * Save tokens to localStorage
   * @param accessToken - Access token
   * @param refreshToken - Refresh token
   */
  saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  /**
   * Save user data to localStorage
   * @param user - User object
   */
  saveUser(user: any): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  /**
   * Get tokens from localStorage
   * @returns Object with tokens
   */
  getTokens(): { accessToken: string | null; refreshToken: string | null } {
    return {
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken'),
    };
  }

  /**
   * Get user from localStorage
   * @returns User object or null
   */
  getUser(): any {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Persist today's date as the session start date
   */
  saveLoginDate(): void {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem('loginDate', today);
  }

  /**
   * Returns true if the stored login date belongs to a previous calendar day
   */
  isSessionExpiredByDate(): boolean {
    const stored = localStorage.getItem('loginDate');
    if (!stored) return false;
    const today = new Date().toISOString().slice(0, 10);
    return stored < today;
  }

  /**
   * Clear all auth data from localStorage
   */
  clearAuthData(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('loginDate');
  }
}

export default new AuthService();

